import { splitChangelog } from '@erp/workflows';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  SealCheckIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { WithChangelogDetails } from './ChangelogText';
import { cn } from 'src/lib/utils';

interface VersionBadgeProps {
  /** The Workflow (lineage) id this badge links to. */
  workflowId: string;
  /** Human-displayable version number (e.g. 3 → "v3"). */
  version?: number;
  /** True when this version was pinned at display time. When true we link to
   * the bare workflow URL (pinned view); otherwise we link to the version URL. */
  isPinned?: boolean;
  /** Optional AI-generated changelog; shown as tooltip content when present. */
  changelog?: string | null;
  /** Status pill — omit to just show the version number. */
  status?: 'draft' | 'finalized';
  /** When true, render as plain inline text instead of a link. */
  nonInteractive?: boolean;
  /** Extra classes for the badge element. */
  className?: string;
  /** Compact visual mode (smaller padding, no "Published" word, just the seal icon). */
  compact?: boolean;
}

/**
 * Badge that identifies which version of a workflow something is referencing.
 *
 * Use it on a run row, a step run, an entity-context panel, or anywhere else
 * that has a `workflowId` (and optionally a version) but doesn't want to fetch
 * the full WorkflowDefinition just to display "v3". When `isPinned` is true we
 * link to the pinned-view URL (`/workflows/$workflowId`); otherwise we link to
 * the specific-version URL (`/workflows/$workflowId/v/$versionNumber`).
 */
export function VersionBadge({
  workflowId,
  version,
  isPinned,
  changelog,
  status,
  nonInteractive,
  className,
  compact,
}: VersionBadgeProps) {
  const gt = useGT();
  const label =
    status === 'draft'
      ? gt('Draft', { context: 'workflow version badge status for draft' })
      : typeof version === 'number'
        ? `v${version}`
        : gt('v?', {
            context: 'workflow version badge when version number is unknown',
          });

  const badge = (
    <Badge
      variant={status === 'draft' ? 'secondary' : 'outline'}
      className={cn(
        'inline-flex items-center gap-1 font-mono text-xs',
        compact && 'h-5 px-1.5 py-0',
        className,
      )}
    >
      {label}
      {isPinned && (
        <SealCheckIcon
          className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'}
          weight="fill"
        />
      )}
      {!compact && isPinned && (
        <span>
          <T context="workflow version badge published label shown next to seal icon">
            Published
          </T>
        </span>
      )}
    </Badge>
  );

  // Tooltip shows the changelog if available — the "why did this run do that?"
  // answer, right at the point the user sees the version reference.
  const withTooltip = changelog ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{badge}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex items-start gap-1.5">
          <ClockCounterClockwiseIcon className="mt-0.5 h-3 w-3 flex-shrink-0" />
          <span className="text-xs">{splitChangelog(changelog).subject}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  ) : (
    badge
  );

  if (nonInteractive) {
    return (
      <WithChangelogDetails changelog={changelog}>
        {withTooltip}
      </WithChangelogDetails>
    );
  }

  // Pinned versions link to the bare workflow URL; specific versions include
  // the `/v/$versionNumber` segment. Drafts are treated as specific versions
  // since there's no "the pinned draft" — if we don't know the version we fall
  // back to the pinned URL so the user lands on something sensible.
  if (isPinned || typeof version !== 'number') {
    return (
      <WithChangelogDetails changelog={changelog}>
        <Link
          to="/workflows/$workflowId"
          params={{ workflowId }}
          className="inline-flex hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
        >
          {withTooltip}
        </Link>
      </WithChangelogDetails>
    );
  }

  return (
    <WithChangelogDetails changelog={changelog}>
      <Link
        to="/workflows/$workflowId/v/$versionNumber"
        params={{ workflowId, versionNumber: String(version) }}
        className="inline-flex hover:opacity-80"
        onClick={(e) => e.stopPropagation()}
      >
        {withTooltip}
      </Link>
    </WithChangelogDetails>
  );
}
